import ActionParseable from "./class.ActionParseable";

/**
 * This is an abstract class. Any class that inherits from MotorParseable must implement certain methods listed below.
 */
export default class MotorParseable extends ActionParseable {
	// Abstract methods to implement (some are optional)

	/**
	 * Returns a new instance of the driver associated with this Action
	 * @returns {Motors.Driver}
	 */
	getNewDriver() {
		throw new Error("Abstract method not implemented.");
	}

	/**
	 * Returns the key of the motor output which will be parsed to determine correctness
	 * @returns {string}
	 */
	getAnswerKey() {
		return this.model.answerKey;
	}

	/**
	 * Returns the key of the motor output which will be displayed in the student response bubble
	 * @returns {string}
	 */
	getPreviewKey() {
		return this.model.previewKey;
	}

	/**
	 * Returns an Object with the inputs that will be sent to the motor upon initialization
	 */
	getStartSignal() {
		throw new Error("Abstract method not implemented.");
	}

	/**
	 * Returns an Object with the inputs that will be sent to the motor when it is submitted.
	 * Can be used to disable the motor.
	 */
	getDisabledSignal() {
		return {};
	}

	/**
	 * If this returns true, pointer events on the motor's container div will be set to none
	 * before the motor is instantiated.
	 * @returns {boolean}
	 */
	shouldInteractionsBeDisabled() {
		return false;
	}

	/**
	 * In a lesson replay, this returns an Object that
	 * will be sent to the motor's inputs to simulate the student's response.
	 * @param {*} data an Object containing the cached response (same format as motor output)
	 */
	getCachedResponseSignal(data) {
		throw new Error("Abstract method not implemented.");
	}

	onRenderResponse($responseDiv) {
		this.setSubmittable(this.isResponseSubmittable());
	}

	onRenderSubmit($submitDiv) {
		if (this.model.previewKey === "noPreview") {
			$submitDiv.html("I'm done.");
		}
	}

	/**
	 * Abstract function for clear button default appearance.
	 * Needs to be set by specific motors before constructor,
	 * which is why this is a function.
	 */
	hasClearButtonDefault() {
		return false;
	}

	/**
	 * Abstract function for how to tell if the clear button should
	 * be enabled (red) or disabled (grey).
	 */
	isClearButtonEnabled() {
		return false;
	}

	// End abstract methods

	/**
	 * Update the clear button's enabled-ness.
	 */
	rerenderClearButton() {
		if (this.$clearButton) {
			if (this.isClearButtonEnabled()) {
				this.$clearButton.addClass("is-enabled");
			} else {
				this.$clearButton.removeClass("is-enabled");
			}
		}
	}

	/**
	 * Refresh driver outputs and send the motor fresh starting data.
	 */
	onClearButtonClick() {
		if (this.isClearButtonEnabled()) {
			this.driverOutputs = {};
			const startSignal = this.getStartSignal();
			this.controller.driver.outputs.sendData(startSignal);
		}
	}

	/**
	 * A helper function for parsing JSON strings. Displays any parsing errors in a modal.
	 * Should be used in lieu of JSON.parse whenever possible.
	 * @param {string} str The string you'd like to parse.
	 * @param {(result: any) => any} [transformResultFn]
	 * 	A function which receives the result, and returns a result.
	 * 	This is where you may transform the result if you are unhappy with it.
	 */
	tryJSONParse(str, transformResultFn) {
		let result;
		try {
			result = JSON.parse(str);
		} catch (e) {
			console.warn(e);

			if (this.mainController.isPreviewMode) {
				$(
					`<h3>JSON Parsing Error</h3> <p>${str}</p> <p>${e.message}</p>`
				).showModal();
			}
		} finally {
			if (transformResultFn) {
				result = transformResultFn(result);
			}
		}
		return result;
	}

	constructor(options) {
		super(options);

		// Used to keep track of the motor state between attempts.
		this.driverOutputs = {};

		this.initialize();

		// A reference to a Motor controller.
		this.controller = null;

		// Keep track of the problem div so we can shut off pointer events.
		this.$problemDiv = null;

		// Which keys?
		this.answerKey = this.getAnswerKey();
		this.previewKey = this.getPreviewKey();

		// Is there a cached response to this problem?
		this.cachedResponse = null;

		// Whether the current motor has resolved anything
		// (If the first attempt is wrong, this will be false before the second motor resolves for the first time)
		this.hasMotorResolved = false;

		// Whether to render a clear button.
		if (this.model.hasClearButton !== undefined) {
			this.hasClearButton = this.model.hasClearButton;
		} else {
			this.hasClearButton = this.hasClearButtonDefault();
		}
		this.$clearButton = null;

		if (Motors) {
			this.render();
		} else {
			this.onAdvance(this.$anchor);
		}
	}

	/**
	 * Handles any necessary initializtion.
	 */
	initialize() {
		// We need to bind this listener so we're able to add and remove it from
		// the motor's event listener.
		this.motorListener = _.bind(this.motorListener, this);
	}

	/**
	 * Handles resolution of data from the motor driver.
	 *
	 * @param {*} data
	 */
	async motorListener(data) {
		const key = this.pointerStr + this.attempt;
		Object.assign(this.driverOutputs, data);
		this.inputController.motorCache[key] = JSON.stringify(this.driverOutputs);

		// Don't render the response bubble when the motor resolves for the first time
		// The first resolve is caused by sending the start signal
		// This prevents the problem and response bubble from appearing simultaneously
		if (this.hasMotorResolved) {
			await this.renderResponseBubble(
				this.driverOutputs[this.answerKey],
				this.driverOutputs[this.previewKey]
			);
		} else {
			this.hasMotorResolved = true;
		}
	}

	displayVerificationErrors(startSignal) {
		const verificationErrors = this.controller.driver.verify(startSignal);
		if (verificationErrors.length > 0) {
			$(`
					<h3>Verification Errors</h3>
					<ul>
						${verificationErrors.map((errMsg) => `<li>${errMsg}</li>`).join("\n")}
					</ul>
				`).showModal();
		}
	}

	onRenderProblem($problemDiv) {
		if (this.shouldInteractionsBeDisabled()) {
			$problemDiv.addClass("disabled");
		}

		/**
		 * Build the motor.
		 */
		const driver = this.getNewDriver();
		this.controller = new Motors.Controller({driver});
		this.hasMotorResolved = false;

		/**
		 * Build the parsing stuff.
		 */
		let advance = false;
		let loadCachedResponse = false;
		if (this.isParsed) {
			// If this is a parsed thingy.
			if (this.inputController.motorCache[this.pointerStr + this.attempt]) {
				loadCachedResponse = true;
			} else {
				this.controller.driver.addResolveListener(this.motorListener);
			}
		} else {
			advance = true;
		}

		// Load cached response from inputController
		if (!advance && loadCachedResponse) {
			this.cachedResponse = JSON.parse(
				this.inputController.motorCache[this.pointerStr + this.attempt]
			);
			this.driverOutputs = this.cachedResponse;
		}

		this.controller.driver.addResolveListener(() => {
			if (this.taItem) {
				this.taItem.setInteracted();
			}
		});

		const startSignal = this.getStartSignal();
		this.controller.start(startSignal);
		$problemDiv.append("<div style='max-width:100%;' />");
		Motors.render(this.controller.render(), $problemDiv.children()[0]);

		if (this.mainController.isPreviewMode) {
			this.displayVerificationErrors(startSignal);
		}

		$problemDiv
			.attr("tabindex", 0)
			.focus(() => {
				$problemDiv.css("outline", "none");
			})
			.keypress((event) => {
				// Bind enter key to submit.
				if (event.keyCode === 13) {
					if (this.responseView && this.submittable) {
						this.responseView.$el
							.find(`#${this.pointerStr}Attempt${this.attempt}Submit`)
							.trigger("click");
					}
				}
			})
			.focus();

		/**
		 * If this motor has a clear-all button, add it after the motor.
		 */
		if (this.hasClearButton) {
			const $clearWrapper = $("<div class='clear-button-wrapper'></div>");
			const clearText = "Reset";
			const $clearButton = $(
				"<div class='clear-button'>" + clearText + "</div>"
			);
			$clearButton.click(() => {
				this.onClearButtonClick();
				this.rerenderClearButton();
			});

			this.$clearButton = $clearButton;
			$clearWrapper.append($clearButton);
			$problemDiv.append($clearWrapper);
			this.rerenderClearButton();
		}

		this.$problemDiv = $problemDiv;
	}

	async postRenderProblem() {
		if (!this.isParsed) {
			this.passProblem(this.problemView.$el);
		} else if (this.cachedResponse !== null) {
			this.driverOutputs = this.cachedResponse;
			this.cachedResponse = null;
			this.controller.driver.addResolveListener(this.motorListener);
			const cachedResponseSignal = this.getCachedResponseSignal(
				this.driverOutputs
			);

			this.controller.driver.outputs.sendData(cachedResponseSignal);

			await this.renderResponseBubble(
				this.driverOutputs[this.answerKey],
				this.driverOutputs[this.previewKey]
			);
		} else {
			await this.renderResponseBubble();
		}
	}

	onSubmit() {
		if (this.controller) {
			this.controller.driver.removeResolveListener(this.motorListener);
			this.controller.driver.outputs.sendData(this.getDisabledSignal());
			// this.controller.stop();	// .stop() prevents motors from rerendering when disabled.
			this.controller = null;
			this.$problemDiv.addClass("disabled");
			this.$problemDiv = null;
		}
	}
}
